import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, IndianRupee, Sparkles, BadgeCheck } from "lucide-react";
import * as Icons from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const CATEGORY_META = {
  welder: { color: "orange", icon: "Flame" },
  fitter: { color: "green", icon: "Wrench" },
  electrician: { color: "yellow", icon: "Zap" },
  plumber: { color: "blue", icon: "Droplets" },
  rigger: { color: "stone", icon: "Anchor" },
  technician: { color: "indigo", icon: "Cog" },
  helper: { color: "purple", icon: "HandHelping" },
  mason: { color: "amber", icon: "Hammer" },
};

export default function JobCard({ job }) {
  const { t } = useI18n();
  const meta = CATEGORY_META[job.category] || CATEGORY_META.helper;
  const Icon = Icons[meta.icon] || Icons.Wrench;
  const isPremium = !!job.premium_paid;
  const isBoosted = job.boosted_until && job.boosted_until > new Date().toISOString();
  return (
    <Link
      to={`/jobs/${job.id}`}
      data-testid={`job-card-${job.id}`}
      className={`sarthi-card sarthi-card-press block p-5 ${isBoosted ? "ring-2 ring-fuchsia-500" : isPremium ? "ring-2 ring-sarthi-primary/60" : ""}`}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className={`flex h-14 w-14 items-center justify-center rounded-xl cat-${meta.color}`}>
          {(job.logo_file_id || (job.logo_url && isPremium)) ? (
            <img
              src={job.logo_file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${job.logo_file_id}` : job.logo_url}
              alt=""
              className="h-12 w-12 rounded-lg object-cover"
            />
          ) : (
            <Icon className="h-7 w-7" strokeWidth={2.5} />
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {isBoosted && (
            <span
              data-testid={`boosted-badge-${job.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm"
            >
              <Icons.Zap className="h-3 w-3" strokeWidth={3} /> Boosted
            </span>
          )}
          {isPremium && (
            <span
              data-testid={`premium-badge-${job.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white shadow-sm"
            >
              <Sparkles className="h-3 w-3" strokeWidth={3} /> Premium
            </span>
          )}
          <div className="rounded-full bg-sarthi-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-sarthi-primary">
            {t(`categories_names.${job.category}`)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <h3 className="font-display text-xl font-extrabold leading-tight text-sarthi-text">{job.title}</h3>
        {job.contractor_verified && (
          <span
            data-testid={`verified-badge-${job.id}`}
            title="Verified Recruiter"
            className="inline-flex flex-none items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200"
          >
            <BadgeCheck className="h-3 w-3" strokeWidth={3} /> Verified
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm font-semibold text-sarthi-muted">
        <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{job.location}</span>
        <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" />{job.positions || 1}</span>
        <span className="inline-flex items-center gap-1 font-bold text-sarthi-secondary">
          <IndianRupee className="h-4 w-4" />{job.salary_min}–{job.salary_max}
        </span>
      </div>
    </Link>
  );
}
